import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/**
 * Compact region rollup used on the Dashboard.
 * Fetches /api/reports/region-dashboard and renders a table on desktop /
 * stacked cards on mobile. Only shows regions the caller can see records for.
 */
export default function RegionSummaryWidget() {
  const { user } = useAuth();
  const showQ = can(user, "quotations", "view");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/reports/region-dashboard");
        setRows(res.data?.rows || []);
      } catch (_e) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totals = rows.reduce(
    (acc, r) => ({
      leads: acc.leads + (r.leads || 0),
      active_quotations: acc.active_quotations + (r.active_quotations || 0),
      upcoming_demos: acc.upcoming_demos + (r.upcoming_demos || 0),
    }),
    { leads: 0, active_quotations: 0, upcoming_demos: 0 },
  );

  return (
    <section className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="region-summary-widget">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-sky-50 text-sky-700 flex items-center justify-center">
            <MapPin className="w-4 h-4" />
          </div>
          <div>
            <div className="font-heading font-semibold text-slate-900">Regions at a glance</div>
            <div className="text-xs text-slate-500">Open pipeline · active quotes · upcoming demos, by hub</div>
          </div>
        </div>
        <Link to="/leads?view=byregion" className="text-xs text-indigo-600 hover:text-indigo-800 hidden sm:inline-flex items-center gap-1" data-testid="region-widget-details-link">
          Details <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-slate-400 text-center" data-testid="region-widget-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-sm text-slate-400 text-center" data-testid="region-widget-empty">
          No region data yet. Set a region on your Leads to see the rollup here.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Region</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Open leads</TableHead>
                  {showQ && <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Active quotes</TableHead>}
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Upcoming demos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.region_id || "__none__"} data-testid={`region-widget-row-${r.region_id || "none"}`}>
                    <TableCell className="font-medium text-slate-900 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                        {r.region_name}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm text-slate-700 font-medium">{r.leads}</TableCell>
                    {showQ && <TableCell className="text-right text-sm text-slate-700 font-medium">{r.active_quotations}</TableCell>}
                    <TableCell className="text-right text-sm text-slate-700 font-medium">{r.upcoming_demos}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50">
                  <TableCell className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-slate-900" data-testid="region-widget-total-leads">{totals.leads}</TableCell>
                  {showQ && <TableCell className="text-right text-sm font-semibold text-slate-900" data-testid="region-widget-total-quotes">{totals.active_quotations}</TableCell>}
                  <TableCell className="text-right text-sm font-semibold text-slate-900" data-testid="region-widget-total-demos">{totals.upcoming_demos}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          {/* Mobile stacked cards */}
          <ul className="md:hidden divide-y divide-slate-100">
            {rows.map((r) => (
              <li key={r.region_id || "__none__"} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`region-widget-card-m-${r.region_id || "none"}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />
                  <span className="font-medium text-slate-900 text-sm truncate">{r.region_name}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600 shrink-0">
                  <span title="Open leads"><b className="text-slate-900">{r.leads}</b> ld</span>
                  {showQ && <span title="Active quotes"><b className="text-slate-900">{r.active_quotations}</b> qt</span>}
                  <span title="Upcoming demos"><b className="text-slate-900">{r.upcoming_demos}</b> dm</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
